import React, { useState, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminFaqsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin FAQs';
  }, []);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  const categoryDropdownRef = useRef(null);

  // Patient FAQs data
  const patientFaqData = [
    {
      id: 1,
      question: 'How do I change my password?',
      answer: 'You can change your password by going to your account settings and selecting the password change option.',
      category: 'Account'
    },
    {
      id: 2,
      question: 'How do I make a booking?',
      answer: 'You can make a booking by visiting the Bookings section and selecting your preferred appointment time.',
      category: 'Booking'
    },
    {
      id: 3,
      question: 'What are your operating hours?',
      answer: 'Our telehealth services are available 24/7, while in-person appointments are available during business hours.',
      category: 'General'
    }
  ];

  // Admin FAQs data
  const adminFaqData = [
    {
      id: 4,
      question: 'How do I manage user accounts?',
      answer: 'User accounts can be managed through the Users section in the admin dashboard. You can create, edit, and deactivate accounts.',
      category: 'User Management'
    },
    {
      id: 5,
      question: 'How do I generate reports?',
      answer: 'Reports can be generated from the Reports section. Select your criteria and click Generate to create custom reports.',
      category: 'Reporting'
    },
    {
      id: 6,
      question: 'How do I backup system data?',
      answer: 'System backups can be initiated from the System Settings. Choose full or incremental backup options.',
      category: 'System'
    }
  ];

  // Doctor FAQs data
  const doctorFaqData = [
    {
      id: 7,
      question: 'How do I access patient records?',
      answer: 'Patient records are accessible through the Patients section. Click on a patient to view their complete medical history.',
      category: 'Records'
    },
    {
      id: 8,
      question: 'How do I schedule appointments?',
      answer: 'Appointments can be scheduled through the Calendar section. Select time slots and assign patients.',
      category: 'Scheduling'
    },
    {
      id: 9,
      question: 'How do I update prescription information?',
      answer: 'Prescriptions can be updated in the patient record under the Medications tab.',
      category: 'Prescriptions'
    }
  ];

  // Clinician FAQs data
  const clinicianFaqData = [
    {
      id: 10,
      question: 'How do I record patient vitals?',
      answer: 'Patient vitals can be recorded in the patient file under the Vitals section during appointments.',
      category: 'Patient Care'
    },
    {
      id: 11,
      question: 'How do I access care protocols?',
      answer: 'Care protocols are available in the Resources section, organized by medical specialty and condition.',
      category: 'Protocols'
    },
    {
      id: 12,
      question: 'How do I submit patient assessments?',
      answer: 'Patient assessments can be submitted through the Assessment forms in each patient record.',
      category: 'Assessments'
    }
  ];

  const handleViewAll = (type) => {
    // In a real app, this would navigate to a full FAQ page for that type
    console.log(`Viewing all ${type} FAQs`);
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-2 px-6 pb-6">
        {/* First Block - Filter Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex gap-6">
            {/* Filter Category - 30% */}
            <div className="w-[30%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Category:
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                    {selectedCategories.length === 0 ? (
                      <span className="text-gray-400 text-sm">Please Select</span>
                    ) : (
                      selectedCategories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {category}
                          <button
                            onClick={() => handleCategoryRemove(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    onClick={handleAddCategory}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Category Dropdown */}
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {['Account', 'Booking', 'General', 'User Management', 'Reporting', 'System', 'Records', 'Scheduling', 'Prescriptions', 'Patient Care', 'Protocols', 'Assessments'].map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        disabled={selectedCategories.includes(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* I want to - 50% */}
            <div className="w-[50%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                I want to:
              </label>
              <input
                type="text"
                placeholder="I want to..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* FAQ Blocks Grid - 2x2 */}
        <div className="grid grid-cols-2 gap-6">
          {/* Patient FAQs Block */}
          <div className="bg-white border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-blue-600 mb-2">Patient FAQs</h2>
            <div className="border-b-2 border-blue-600 mb-4"></div>
            <div className="space-y-4">
              {patientFaqData.map((faq) => (
                <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                  <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                  <p className="text-gray-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => handleViewAll('Patient')}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition-colors"
              >
                View All
              </button>
            </div>
          </div>

          {/* Admin FAQs Block */}
          <div className="bg-white border border-green-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-green-600 mb-2">Admin FAQs</h2>
            <div className="border-b-2 border-green-600 mb-4"></div>
            <div className="space-y-4">
              {adminFaqData.map((faq) => (
                <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                  <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                  <p className="text-gray-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => handleViewAll('Admin')}
                className="text-green-600 hover:text-green-800 font-medium text-sm border border-green-600 px-4 py-2 rounded hover:bg-green-50 transition-colors"
              >
                View All
              </button>
            </div>
          </div>

          {/* Doctor FAQs Block */}
          <div className="bg-white border border-purple-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-purple-600 mb-2">Doctor FAQs</h2>
            <div className="border-b-2 border-purple-600 mb-4"></div>
            <div className="space-y-4">
              {doctorFaqData.map((faq) => (
                <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                  <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                  <p className="text-gray-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => handleViewAll('Doctor')}
                className="text-purple-600 hover:text-purple-800 font-medium text-sm border border-purple-600 px-4 py-2 rounded hover:bg-purple-50 transition-colors"
              >
                View All
              </button>
            </div>
          </div>

          {/* Clinician FAQs Block */}
          <div className="bg-white border border-orange-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-orange-600 mb-2">Clinician FAQs</h2>
            <div className="border-b-2 border-orange-600 mb-4"></div>
            <div className="space-y-4">
              {clinicianFaqData.map((faq) => (
                <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                  <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                  <p className="text-gray-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => handleViewAll('Clinician')}
                className="text-orange-600 hover:text-orange-800 font-medium text-sm border border-orange-600 px-4 py-2 rounded hover:bg-orange-50 transition-colors"
              >
                View All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>
    </div>
  );
};

export default AdminFaqsPage;